import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import type { Todo } from '../api/types'
import { TodoItem } from './TodoItem'
import { TodoInput } from './TodoInput'
import { formatDate } from '../lib/parse'
import { sortTodos } from '../lib/todoSort'
import { useUIStore } from '../store/ui'

interface DayColumnProps {
  date: Date
  todos: Todo[]
  isToday: boolean
}

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function DayColumn({ date, todos, isToday }: DayColumnProps) {
  const dateStr = formatDate(date)
  const dayName = DAY_NAMES[date.getDay()]
  const dayNum = date.getDate()
  const monthName = MONTH_NAMES[date.getMonth()]

  const smartStatusSort = useUIStore((s) => s.smartStatusSort)
  const sortedTodos = sortTodos(todos, smartStatusSort)
  const todoIds = sortedTodos.map((t) => t.id)

  // Make this column a droppable zone (especially important for empty columns)
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `day-${dateStr}`,
    data: { date: dateStr, type: 'day' },
  })

  return (
    <div
      ref={setDropRef}
      style={{
        flex: '1 1 0',
        minWidth: '120px',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--color-border)',
        padding: '0 4px',
        boxShadow: isOver ? 'inset 2px 0 0 var(--color-accent)' : 'none',
        background: 'transparent',
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* Date header — fixed height so all columns stay aligned.
          The month name is inline with the eyebrow (A3.3) instead of a
          separate stacked block, so today's header stays the same height.
          The `day-col-header` class marks this strip as the grab handle for
          the horizontal window-shift drag (handled in WeeklyGrid). */}
      <div
        className="day-col-header"
        style={{
          padding: '20px 12px 12px',
          borderBottom: '1px solid var(--color-border)',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'pan-y',
        }}
      >
        <div
          className="eyebrow"
          style={{
            color: isToday ? 'var(--color-accent)' : 'var(--color-ink-faint)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>{dayName}</span>
          {isToday && (
            <span style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              textTransform: 'none',
              letterSpacing: 'normal',
              fontSize: '0.75rem',
              color: 'var(--color-accent)',
            }}>
              {monthName}
            </span>
          )}
        </div>
        <div style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'var(--text-day-num)',
          fontWeight: 500,
          lineHeight: 1.05,
          color: isToday ? 'var(--color-ink)' : 'var(--color-ink-muted)',
          letterSpacing: '-0.02em',
          marginTop: '2px',
        }}>
          {dayNum}
        </div>
      </div>

      {/* Todo list — TodoInput lives inside this scroll container (A3.4)
          so it sits directly after the last todo, not pinned to the bottom.
          min-height: 100% ensures an empty column still provides a large
          drop target for cross-column DnD (A3.5). */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', minHeight: '100%' }}>
        <SortableContext items={todoIds} strategy={verticalListSortingStrategy}>
          {sortedTodos.map((todo) => (
            <TodoItem key={todo.id} todo={todo} />
          ))}
        </SortableContext>

        {/* + add input follows the list in document flow (A3.4).
            The bare placeholder is its own empty-state cue, so no em-dash needed. */}
        <div style={{ marginTop: '8px' }}>
          <TodoInput
            defaultDate={dateStr}
            todos={todos}
            placeholder="+ add"
          />
        </div>
      </div>
    </div>
  )
}
